import moment, {Moment} from 'moment';
import {get} from '../api/api';
import {
  uriCard,
  uriGetBus,
  uriGetSinisterDocuments,
  uriUserBuV2,
} from '../api/endpoints';
import {Store} from '../store';
import {setBusMap} from '../store/actions';
import colors from './colors';
import {functionalitiesAlias, strings} from './strings';
import {ibanRegexes} from './constants';

export interface ICurrencyCode {
  id: string;
  currencyCodeRios: string;
  currencyCharacter: string;
}

export function createClassName(defaultClass: string, classes?: string) {
  return classes ? `${classes} ${defaultClass}` : defaultClass;
}

export function today(endDay = false) {
  const date = new Date();
  if (endDay) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date;
}

export function has18Years(dateOfBirth: Date) {
  const stringsDefault = Store.getState().stringsDefault.value;
  if (stringsDefault) {
    return (
      moment().diff(moment(dateOfBirth), 'years', false) >=
      parseInt(stringsDefault.pt.register.register_minAge)
    );
  } else {
    return (
      moment().diff(moment(dateOfBirth), 'years', false) >=
      parseInt(strings.pt.register.register_minAge)
    );
  }
}

export function getDateInServerFormat(date: Date) {
  return moment(date).format(process.env.REACT_APP_SERVER_DATE_FORMAT);
}

export function censorWord(word: string, startlimit = 3, endLimit = 3) {
  return (
    word.slice(0, startlimit) +
    '*'.repeat(word.length - startlimit - endLimit) +
    word.slice(endLimit * -1)
  );
}

export function censorEmail(email: string) {
  var arr = email?.split('@');
  var limit = 3;
  if (arr[0].length < 7) {
    var numCensor = arr[0].length / 2;
    limit = Math.round(numCensor) - 1;
  }
  return censorWord(arr[0], limit, limit) + '@' + arr[1];
}

export function censorPhone(phone: string) {
  if (phone[0] === '+') {
    return `(${phone.slice(0, 4)})${censorWord(phone.slice(4), 1, 3)}`;
  } else {
    return censorWord(phone, 1, 3);
  }
}

export function formatPhone(phone: string) {
  return phone?.replace(/(\d{3})\D?(\d{3})\D?(\d{3})/, '$1 $2 $3');
}

export function createGUID() {
  var guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    }
  );
  return guid;
}

export function decodeToken(token: any) {
  var base64Url = token?.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    atob(base64)
      ?.split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export function convertRemToPixels(rem: number) {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

export const transformStrings = (
  lang: any,
  stringProp: any,
  stringObject: any
) => {
  let stringToUpdate = (strings as any)[lang][stringProp];
  stringObject.forEach((el: any) => {
    stringToUpdate[el.labelTemplateAlias] = el.text;
  });
  return stringToUpdate;
};

interface Label {
  id: number;
  buAlias: string;
  labelTemplateAlias: string;
  languageAlias: string;
  text: string;
  type: string;
  url: string | null;
}

interface Functionality {
  id: number;
  featureTemplateAlias: string;
  featureTemplateTitle: string;
  buAlias: string;
  isVisible: boolean;
  status: string;
  labels: Label[];
}

interface StringFeature {
  [key: string]: {
    [key: string]: {
      [key: string]: string | {type: string; text: string};
    };
  };
}

export function updateStringFeatureWithLabels(
  stringFeature: StringFeature,
  functionalities: Functionality[]
): StringFeature {
  const sectionNamesSet = new Set<string>();
  var buFeatureStrings: any;
  functionalities?.forEach((functionality) => {
    const featureTemplateAlias = (functionalitiesAlias as any)[
      functionality.featureTemplateAlias
    ];
    sectionNamesSet.add(featureTemplateAlias);
    buFeatureStrings = stringFeature['pt'] || {};
    const sectionDict = buFeatureStrings[featureTemplateAlias] || {};

    functionality.labels.forEach((label) => {
      const labelTemplateAlias = label.labelTemplateAlias;
      const labelText = label.text;
      const key = labelTemplateAlias;
      sectionDict[key] = labelText;
    });
    buFeatureStrings[featureTemplateAlias] = sectionDict;
  });

  return {...stringFeature, pt: {...stringFeature.pt, ...buFeatureStrings}};
}

export const updateStrings = (funcionalidade: any) => {
  var result: any = {
    pt: {},
  };

  funcionalidade.forEach((item: any) => {
    const labels: any = {};

    if (item.labels?.length > 0) {
      item.labels?.forEach((el: any) => {
        if (el.url) {
          labels[el.labelTemplateAlias] = el.url;
        } else {
          labels[el.labelTemplateAlias] = el.text;
        }
      });

      result.pt[(functionalitiesAlias as any)[item.featureTemplateAlias]] =
        labels;
    }
  });

  return result;
};

export const recreateFilesFromRedux = (files: any) => {
  return files.map((file: any) => {
    const newFile = new File([file.buffer], file.path);
    // const newFile = new Blob([file.buffer])
    const preview = URL.createObjectURL(newFile);
    return {...newFile, ...file};
    //return newFile
  });
};

export const createCoveragesMap = (coverages: any[]) => {
  let coveragesMap: any = {};

  coverages.forEach((coverage: any) => {
    let aux = coveragesMap[`${coverage.catCode}`];

    if (aux) {
      aux.medicalActs = [
        ...aux.medicalActs,
        {
          procCode: coverage.procCode,
          procCodeDesc: coverage.procCodeDesc,
          msg: coverage.msg,
          type: coverage.type,
        },
      ];
    } else {
      aux = {
        catCode: coverage.catCode,
        catCodeDesc: coverage.catCodeDesc,
        medicalActs: [
          {
            procCode: coverage.procCode,
            procCodeDesc: coverage.procCodeDesc,
            msg: coverage.msg,
            type: coverage.type,
          },
        ],
      };
    }

    coveragesMap[`${coverage.catCode}`] = aux;
  });

  return coveragesMap;
};

export const sortArrayByParam = (array: any[], param: string) => {
  return array.sort((a: any, b: any) => {
    return a[`${param}`].localeCompare(b[`${param}`]);
  });
};

export const toDataUrl = (url: string, callback: Function) => {
  fetch(url, {
    method: 'GET',
  })
    .then((res) =>
      res.blob().then((res) => {
        const reader = new FileReader();
        reader.onloadend = function () {
          callback(reader.result);
        };
        reader.readAsDataURL(res);
      })
    )
    .catch((e) => callback(url));
};

export const toDataUrlAsync = async (url: any) =>
  await fetch(url)
    .then(async (response) => await response.blob())
    .then(
      async (blob: any) =>
        await new Promise(async (resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

export const numberWithSpaces = (x: string) => {
  return (x && x.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')) || '';
};

export const limitArray = (
  array: any | undefined[],
  maxLimit: number | undefined
) => {
  if (array != undefined && maxLimit != undefined) {
    return array.slice(0, maxLimit);
  } else {
    return array;
  }
};

export const appendFormData = (data: any, root: any, formData: any) => {
  root = root || '';
  if (data instanceof File) {
    formData.append(root, data);
  } else if (Array.isArray(data)) {
    for (var i = 0; i < data.length; i++) {
      appendFormData(data[i], root + '[' + i + ']', formData);
    }
  } else if (typeof data === 'object' && data) {
    for (var key in data) {
      if (data.hasOwnProperty(key)) {
        if (root === '') {
          appendFormData(data[key], key, formData);
        } else {
          appendFormData(data[key], root + '.' + key, formData);
        }
      }
    }
  } else {
    if (data !== null && typeof data !== 'undefined') {
      formData.append(root, data);
    }
  }
  return formData;
};

export const labelsParagraphs = (labels: any) => {
  for (var key in labels) {
    if (labels[key]?.includes('\r\n'))
      labels[key] = labels[key].replaceAll('\r\n', '<p></p>');
  }
};

export const bulletLabelsParagraphs = (labels: any) => {
  for (var key in labels) {
    if (labels[key]?.includes('\n'))
      labels[key] = labels[key].replaceAll('\n', '<p></p>');
  }
};

export const getUserMemId = (availableBus: any, user: any, selectedBu: any) => {
  let busWithNif: any[] = [];
  Object.keys(availableBus).forEach((el: any) => {
    if (availableBus[el].nif === user.nif) {
      busWithNif.push(availableBus[el]);
    }
  });

  let result: any = {memId: '', multicode: ''};

  busWithNif.forEach((element: any) => {
    if (
      selectedBu.famId === element.famId &&
      selectedBu.buId === element.buId
    ) {
      result = {memId: element.memId, multicode: element.multicode};
    }
  });

  return result;
};

export const getColor = (stylesProp: string, color?: any) => {
  return {
    [stylesProp]: color
      ? color
      : Store.getState().globalStyles.value
      ? Store.getState().globalStyles.value.primaryColor
      : colors.blue,
  };
};

export const possibleAppointmentStatus = {
  BOOKED: 'booked',
  PORPOSED: 'porposed',
  CHECKEDIN: 'checked-in',
  PENDING: 'pending',
  FULLFILED: 'fulfilled',
  NOSHOW: 'noshow',
  CANCELLED: 'cancelled',
  DECLINED: 'Declined',
};

export const capitalizeName = (name: any) => {
  const tokens = name.toLowerCase()?.split(' ');
  const words = tokens.filter((el: any) => el !== ' ' && el !== '');
  let capitalized = words
    .map((el: any) => {
      return el[0]?.toUpperCase() + el?.substring(1);
    })
    .join(' ');

  return capitalized;
};

export const scrollToTop = () => {
  window.scrollTo({top: 0, behavior: 'smooth'});
};

export const formatEstimatedPrice = (value: string) => {
  return parseFloat(value) % 1 === 0
    ? value
    : parseFloat(value).toFixed(2).replace('.', ',');
};

export const timeWithTimezone = (time: string) => {
  let dateaux = new Date(time);
  let aux = moment(dateaux).format('YYYY-MM-DDTHH:mm:ss.SSS');
  return moment(aux);
};
export const timeWithTimezoneToApi = (time: string, date: string) => {
  const moment = require('moment-timezone');
  let dateaux = new Date(date);
  let startTimeSplit = time?.split(':');
  let dateSelected = dateaux.setHours(
    parseInt(startTimeSplit[0]),
    parseInt(startTimeSplit[1])
  );

  return moment(dateSelected).tz('Europe/Lisbon').format('HH:mm');
};

export const timeWithTimezoneToDropdown = (time: Moment) => {
  let startTimeZ = time.utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  return moment(startTimeZ);
};

export const getCurrencyCode = (currency: any, feature: string) => {
  const currencyCodes: ICurrencyCode[] = Store.getState().currencies.value;
  const matchingCode = currencyCodes.find(
    (code) => code.currencyCodeRios.toUpperCase() === currency?.toUpperCase()
  );
  if (matchingCode) {
    return matchingCode.currencyCharacter;
  } else {
    return Store.getState()?.strings?.value?.pt[feature]?.default_currency;
  }
};

export const redeUnitToSymbol = (
  unit: string | undefined,
  currency: string | undefined
) => {
  if (unit === 'Eur') {
    return getCurrencyCode(currency, 'myCoverages');
  }
  return unit ? unit : getCurrencyCode(currency, 'myCoverages');
};

export const formatDatePhrase = (date: Date, message: string) => {
  const state = Store.getState();
  const tokens = message?.split(/\[|\]/);

  return tokens.map((tkn: string) => {
    if (tkn === 'DD') {
      return moment(date).format('DD');
    } else if (tkn === 'MM') {
      return moment(date).locale(state.user?.value.language).format('MMMM');
    } else if (tkn === 'HH') {
      return moment(date).format('HH:mm');
    } else {
      return tkn;
    }
  });
};

export const fillBusMap = async (featureName?: string) => {
  const userProfile =
    Store.getState().user?.value.profile || Store.getState().user.value;
  let busResponse: any;
  busResponse = await get(uriGetBus(), false, 0, featureName);

  const createBusMap = (userBus: any, busObj: any) => {
    const bus = busObj?.data?.items;
    const busMap: any = {};

    // creating a new map with the bus and cards from the response
    userBus.forEach((elem: any) => {
      const famId = elem.famId;
      const buId = elem.buId;
      const existingBu = !!busMap[buId];
      const existingGroup = (busMap[buId] && !!busMap[buId][famId]) || false;

      const member = {...elem};
      const memberBu = bus.find((bu: any) => bu.id === buId);
      member.buName = (memberBu && memberBu.name) || '';

      if (existingBu && existingGroup) {
        busMap[buId][famId].push(member);
      } else if (existingBu && !existingGroup) {
        busMap[buId][famId] = [member];
      } else {
        busMap[buId] = {[`${famId}`]: [member]};
      }
    });

    return busMap;
  };
  const addCardDataToBusMap = async (busMap: any, featureName?: string) => {
    // for each card/member of each user's bu obtains the card data and adds it to the busMap
    await Promise.all(
      Object.keys(busMap).flatMap((buId) => {
        return Object.keys(busMap[buId]).map((famId) => {
          return Promise.all(
            busMap[buId][famId].map((member: any, index: number) => {
              return get(
                uriCard(member.buId, member.memId, member.famId),
                false,
                0,
                featureName
              )
                .then((buUserCard: any) => {
                  busMap[buId][famId][index].card = buUserCard?.data;
                })
                .catch((e: any) => {
                  const buUserIndex = busMap[buId][famId].indexOf(member);

                  // if the card api call fails or returns nothing sets the card as {}
                  busMap[buId][famId][buUserIndex].card = {};
                });
            })
          );
        });
      })
    );
  };
  const getBusMap = async (userBus: any, busObj: any, featureName?: string) => {
    const busMap = createBusMap(userBus, busObj);
    await addCardDataToBusMap(busMap, featureName);

    return busMap;
  };
  const setUserAndBusForClient = async (resp: any, featureName?: string) => {
    let userBusResponse = resp;
    let busMap = await getBusMap(userBusResponse, busResponse, featureName);
    Store.dispatch(setBusMap(busMap));
  };

  const resp = (await get(
    uriUserBuV2(userProfile.nif, userProfile.birthdate),
    false,
    0,
    featureName
  )) as any;

  await setUserAndBusForClient(resp?.data?.products, featureName);
};

export const getDaysBetweenDates = (
  fromDate: string,
  toDate: string
): number => {
  const startDate = new Date(fromDate);
  const endDate = new Date(toDate);
  const timeDifference = endDate.getTime() - startDate.getTime();

  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  return Math.abs(Math.round(daysDifference));
};

export const formatCurrencyExpenses = (
  value: number | string,
  currency: string,
  language: string,
  feature: string = 'refund'
) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,00 €';

  const symbol = getCurrencyCode(currency, feature);

  const [intPart, decPart] = num.toFixed(2).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${formattedInt},${decPart} ${symbol}`;
};

export const isRegExpValid = (pattern: any) => {
  if (pattern) {
    try {
      new RegExp(pattern);
      return true;
    } catch (error) {
      return false;
    }
  } else {
    return false;
  }
};
export const joinLanguages = (languages: any[]) => {
  const languagesNames: any[] = [];
  languages.forEach((language) => languagesNames.push(language.name));
  const allLanguages = languagesNames.join(', ');
  return allLanguages;
};

export const joinSpecialities = (specialities: any[]) => {
  const specialitiesNames: any[] = [];
  specialities.forEach((speciality) => specialitiesNames.push(speciality.name));
  const allSpecialities = specialitiesNames.join(', ');
  return allSpecialities;
};

export const getIbanRegex = (countryCode: string): RegExp | null =>
  ibanRegexes[countryCode] || null;

export const isObjectEmpty = (object: Object) => {
  return Object.keys(object).length === 0 && object.constructor === Object;
};

export const generateFuncObjectWithoutLabels = (object: any) => {
  let result: any = [];

  object.forEach((bu: any) => {
    const buFiltered = bu.funcionalidade.map((feature: any) => {
      const {labels, ...filteredObject} = feature;
      return filteredObject;
    });

    return (result[bu.buId] = buFiltered);
  });

  return result;
};

export const filterActiveCategories = (
  inactiveCategoriesLabel: string,
  categories: any[]
) => {
  const catCodes = inactiveCategoriesLabel?.split(',')?.map(Number);

  let categoriesToDisplay: any[] = [];

  categories?.forEach((cat: any) => {
    if (!catCodes?.includes(cat?.code)) {
      categoriesToDisplay?.push(cat);
    }
  });

  return categoriesToDisplay;
};

// Deep merge utility because using just spread operator does not work for nested objects
// Which in turn made it so sometimes keys that were necessary were being overwritten and deleted
// Usage example: const applicableStrings = deepStringObjectMerger(newStrings, stringsDefault, strings);
// The latter object will have the highest priority
export const deepStringObjectMerger = (...objects: any[]) => {
  const result: any = {};
  for (const obj of objects) {
    for (const key in obj) {
      if (
        Object.prototype.hasOwnProperty.call(obj, key) &&
        typeof obj[key] === 'object' &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        result[key] = deepStringObjectMerger(result[key] || {}, obj[key]);
      } else {
        result[key] = obj[key];
      }
    }
  }
  return result;
};

export interface ApiResponse {
  result: {
    code: string;
    status: string;
    message: string;
  };
  data: any;
}

export interface ApiResponseArray {
  result: {
    code: string;
    status: string;
    message: string;
  };
  data: {
    items: any[];
    totalOfItems: number;
  };
}

export const cleanProfessionalNameFromDr = (name: string) => {
  return name.replace(/^\s*(Dr\.?|Dra\.?)\s+/i, '');
};

export const getDocuments = async (
  selectedBu: any,
  selectedItem: any,
  featureName: string,
  setListOfDocuments: Function
) => {
  try {
    await get(
      uriGetSinisterDocuments(selectedBu.buId, selectedItem.auditNbr),
      false,
      0,
      featureName
    ).then((res: any) => {
      setListOfDocuments(res?.data?.items);
    });
  } catch (e: any) {}
  return setListOfDocuments([]);
};

export const getDocumentsForDownload = (listOfDocuments: any[]) => {
  interface IDoc {
    id: string;
    description: string;
    isExpense: boolean;
  }
  let documentsAttached: IDoc[] = [];
  let documentsEmitted: IDoc[] = [];

  if (listOfDocuments) {
    listOfDocuments.map((item: any) => {
      const document = {
        id: item.documentId,
        description: item.fileName,
        isExpense: true,
      };
      if (item.documentType === 'SINISTRO') {
        documentsAttached.push(document);
      }
      if (item.documentType === 'DEV' || item.documentType === 'REC') {
        documentsEmitted.push(document);
      }
    });
  }
  return {
    documentsAttached: documentsAttached,
    documentsEmitted: documentsEmitted,
  };
};
